import { useCallback, useEffect, useRef, useState } from "react";
import { getUiZoom } from "../utilization/ZoomUtilization";

export const IMAGE_ZOOM_MIN_SCALE = 1;
export const IMAGE_ZOOM_MAX_SCALE = 8;
const DOUBLE_CLICK_SCALE = 2.5;
/** One wheel notch. Multiplicative, so a step feels the same at any magnification. */
const WHEEL_STEP = 1.0015;
/**
 * Wheel deltas arrive in three different UNITS and the exponent above is calibrated for pixels.
 *
 * Chrome reports DOM_DELTA_PIXEL (deltaMode 0, deltaY ≈ 100 per notch). Firefox on desktop reports
 * DOM_DELTA_LINE (deltaMode 1, deltaY ≈ 3), which through the same formula is ~0.45% per notch —
 * roughly 200 notches to reach 2.5×, i.e. the feature reads as dead in that browser. DOM_DELTA_PAGE
 * (2) is rarer still and larger again. Normalise to pixels before the exponent.
 */
const WHEEL_LINE_HEIGHT_PX = 16;
const WHEEL_PAGE_HEIGHT_PX = 800;

/**
 * Clamp AFTER normalising. A PAGE-mode notch scales by a viewport height, and a trackpad can emit a
 * single very large pixel delta — either way one event should not jump the whole zoom range at once.
 */
const WHEEL_MAX_DELTA_PX = 200;

const normalizeWheelDeltaToPixels = (deltaY: number, deltaMode: number) => {
  let pixels = deltaY;
  if (deltaMode === 1) pixels = deltaY * WHEEL_LINE_HEIGHT_PX;
  else if (deltaMode === 2) pixels = deltaY * WHEEL_PAGE_HEIGHT_PX;
  return Math.max(-WHEEL_MAX_DELTA_PX, Math.min(WHEEL_MAX_DELTA_PX, pixels));
};

const clampScale = (next: number) =>
  Math.min(IMAGE_ZOOM_MAX_SCALE, Math.max(IMAGE_ZOOM_MIN_SCALE, next));

/**
 * Wheel / pinch zoom + drag pan for one image surface.
 *
 * <p>Shared by the full-screen {@code GlassLightbox} and the handwritten-exam scan viewer, which needs
 * the same gestures INLINE — a grader reading a scanned answer should not have to open an overlay to
 * make the handwriting legible. Two copies of this maths would have drifted the first time either one
 * was touched, and the zoom-unit correction below is exactly the kind of detail that only gets fixed in
 * the copy someone happens to be looking at.
 *
 * <p><b>The unit trap.</b> The app applies {@code zoom: var(--app-font-scale)} to &lt;html&gt; for the
 * "Yazı Boyutu" preference. Under it, {@code getBoundingClientRect()} and pointer {@code clientX/clientY}
 * are VISUAL coordinates (already multiplied by the factor), while the {@code translate()} we produce is
 * in LOCAL pixels the browser multiplies again when painting. Mixing the two makes the image drift away
 * from the cursor for everyone who changed their font size, and it reads as a physics bug rather than a
 * units bug. Every pointer delta is therefore divided by {@link getUiZoom} — a no-op at the default size,
 * and the same correction ChipSelect / DateRangeInput / FilterButton / PreviewPop already apply.
 */
export const useImageZoomPan = () => {
  const [scale, setScale] = useState(IMAGE_ZOOM_MIN_SCALE);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const stageRef = useRef<HTMLDivElement | null>(null);
  // Mirrors of the two state values.
  //
  // The native wheel listener below binds ONCE, so reading `scale` from its closure would pin it at the
  // first value forever — the stale-callback trap for a listener with [] deps.
  //
  // They are also what `zoomAbout` reads instead of a setState updater. Deriving the new offset inside
  // setScale's updater would mean calling setOffset from within it, and an updater must be PURE: React
  // invokes it twice under StrictMode, which applied the pan correction twice and made every wheel notch
  // overshoot. Reading refs and issuing two independent, plain setStates keeps one gesture = one update.
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const pointerMap = useRef<Map<number, { x: number; y: number }>>(new Map());
  const panStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // Read ONCE per gesture rather than per pointermove. getUiZoom() calls getComputedStyle, which forces
  // a style recalc; the factor is the "Yazı Boyutu" preference and cannot change mid-drag, so sampling
  // it on pointerdown is both cheaper and exactly as correct.
  const gestureZoomRef = useRef(1);
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  // True once a drag or pinch actually moved, so a consumer whose backdrop closes on click can tell a
  // pan that ended over it from a real dismissal.
  const movedRef = useRef(false);
  // Bounds movedRef's LIFETIME to the gesture that set it — see endPointer.
  const movedClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A pending clear must not outlive the component — it would write to a ref nobody reads and, in a
  // test, keep a timer open past the assertion.
  useEffect(() => () => {
    if (movedClearTimeoutRef.current !== null) clearTimeout(movedClearTimeoutRef.current);
  }, []);

  const reset = useCallback(() => {
    scaleRef.current = IMAGE_ZOOM_MIN_SCALE;
    offsetRef.current = { x: 0, y: 0 };
    setScale(IMAGE_ZOOM_MIN_SCALE);
    setOffset({ x: 0, y: 0 });
  }, []);

  /**
   * The furthest the image may be translated before it starts leaving the stage.
   *
   * Travel is the half of the SCALED image that overhangs the stage box:
   * `(offsetWidth * scale - clientWidth) / 2`. Measured from the rendered <img> rather than derived
   * from `scale` alone so it stays correct whatever the stage's own box turns out to be — the stage
   * shrink-wraps the image, so at scale 1 this falls out as 0 on both axes naturally (and
   * `Math.max(0, …)` guarantees it).
   *
   * Without it the image could be dragged clean out of view, leaving an empty stage and no hint that
   * the reset button is the way back.
   */
  const clampOffset = (candidate: { x: number; y: number }, forScale: number) => {
    const stage = stageRef.current;
    const image = stage?.querySelector("img");
    if (!stage || !image) return candidate;
    const maxX = Math.max(0, ((image as HTMLImageElement).offsetWidth * forScale - stage.clientWidth) / 2);
    const maxY = Math.max(0, ((image as HTMLImageElement).offsetHeight * forScale - stage.clientHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, candidate.x)),
      y: Math.min(maxY, Math.max(-maxY, candidate.y)),
    };
  };

  /** Zoom about a point, keeping whatever sits under it stationary. */
  const zoomAbout = useCallback((nextScaleRaw: number, clientX: number, clientY: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const uiZoom = getUiZoom();
    const localX = (clientX - (rect.left + rect.width / 2)) / uiZoom;
    const localY = (clientY - (rect.top + rect.height / 2)) / uiZoom;
    const prev = scaleRef.current;
    const next = clampScale(nextScaleRaw);
    if (next === prev) return;
    const prevOffset = offsetRef.current;
    // Back at 1:1 the image fits the stage, so any leftover offset would strand it off-centre.
    const nextOffset =
      next === IMAGE_ZOOM_MIN_SCALE
        ? { x: 0, y: 0 }
        : clampOffset(
            {
              x: localX - (localX - prevOffset.x) * (next / prev),
              y: localY - (localY - prevOffset.y) * (next / prev),
            },
            next,
          );
    // Keep the refs in step immediately: a pinch fires many moves within one frame, and the next one
    // would otherwise re-read the pre-render values and compute its step from a stale base.
    scaleRef.current = next;
    offsetRef.current = nextOffset;
    setScale(next);
    setOffset(nextOffset);
  }, []);

  /**
   * Wheel zoom is bound NATIVELY with `{ passive: false }`, never through React's `onWheel`.
   *
   * React registers wheel on its root container as PASSIVE, so `preventDefault()` from a synthetic
   * handler is ignored (with a console warning) and the page scrolls underneath while the image zooms.
   * A native listener on the stage is the only way to hold the page still.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const deltaPixels = normalizeWheelDeltaToPixels(event.deltaY, event.deltaMode);
      zoomAbout(scaleRef.current * Math.pow(WHEEL_STEP, -deltaPixels), event.clientX, event.clientY);
    };
    stage.addEventListener("wheel", handleWheel, { passive: false });
    return () => stage.removeEventListener("wheel", handleWheel);
  }, [zoomAbout]);

  const distanceBetweenPointers = () => {
    const [a, b] = Array.from(pointerMap.current.values());
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    /*
     * A PRIMARY pointer is the first finger of a fresh gesture, so nothing should still be down.
     * If anything is, it is a GHOST — a pointer whose `pointerup` never reached us. Drop them.
     *
     * This is the recovery half; capturing below is the prevention half. Both are needed: capture can
     * be refused (a detached node, a browser that declines), and one ghost is enough to make the next
     * single-finger drag look like a two-pointer pinch and turn a pan into an erratic zoom.
     */
    if (event.isPrimary) {
      pointerMap.current.clear();
      pinchStart.current = null;
      panStart.current = null;
    }
    pointerMap.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (movedClearTimeoutRef.current !== null) {
      clearTimeout(movedClearTimeoutRef.current);
      movedClearTimeoutRef.current = null;
    }
    movedRef.current = false;
    gestureZoomRef.current = getUiZoom();

    /*
     * Capture EVERY pointer, not just the ones that will pan.
     *
     * The stage shrink-wraps the image while `.alb-root` fills the viewport with 40px of padding, so
     * there is a lot of backdrop around the picture and fingers spreading in a pinch leave the image's
     * box constantly. An uncaptured pointer that lifts out there fires `pointerup` on `.alb-root` —
     * which is the stage's PARENT, and React synthetic events bubble UP, never down — so `endPointer`
     * never runs for it, `pointerMap` never empties, and the gesture-end timer is never even scheduled.
     * Capture retargets the whole sequence to the stage, so a lift anywhere still closes the gesture.
     *
     * Guarded: jsdom has no `setPointerCapture`, and a browser may refuse for a pointer it no longer
     * tracks. Failing here must not abort the gesture — the isPrimary sweep above is the fallback.
     */
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* capture is an optimisation, not a precondition */
    }

    if (pointerMap.current.size === 2) {
      panStart.current = null;
      pinchStart.current = { distance: distanceBetweenPointers(), scale: scaleRef.current };
      return;
    }
    if (scaleRef.current > IMAGE_ZOOM_MIN_SCALE) {
      panStart.current = {
        x: event.clientX,
        y: event.clientY,
        ox: offsetRef.current.x,
        oy: offsetRef.current.y,
      };
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerMap.current.has(event.pointerId)) return;
    pointerMap.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pinchStart.current && pointerMap.current.size >= 2) {
      const distance = distanceBetweenPointers();
      if (distance > 0 && pinchStart.current.distance > 0) {
        const [a, b] = Array.from(pointerMap.current.values());
        const scaleBefore = scaleRef.current;
        zoomAbout(
          pinchStart.current.scale * (distance / pinchStart.current.distance),
          (a.x + b.x) / 2,
          (a.y + b.y) / 2,
        );
        // movedRef tracks VISIBLE movement, and this rests on a specific invariant: zoomAbout never
        // touches `offset` unless `scale` changed, because its `next === prev` early-return sits above
        // the offset computation. That is what makes an at-the-rail pinch (inward at scale 1, outward
        // at scale 8) a genuine no-op rather than a dropped movement. If an offset mutation is ever
        // added ABOVE that early return, this guard silently becomes wrong — the image would move
        // while the flag stayed false.
        if (scaleRef.current !== scaleBefore) movedRef.current = true;
      }
      return;
    }

    if (!panStart.current) return;
    const uiZoom = gestureZoomRef.current;
    const dx = (event.clientX - panStart.current.x) / uiZoom;
    const dy = (event.clientY - panStart.current.y) / uiZoom;
    // A couple of pixels of slop so a click with a shaky hand still registers as a click.
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) movedRef.current = true;
    const nextOffset = clampOffset(
      { x: panStart.current.ox + dx, y: panStart.current.oy + dy },
      scaleRef.current,
    );
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
  };

  const endPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerMap.current.delete(event.pointerId);
    if (pointerMap.current.size < 2) pinchStart.current = null;
    if (pointerMap.current.size === 0) {
      panStart.current = null;
      /*
       * Bound movedRef to the gesture that set it.
       *
       * Consumers read this flag from a CLICK handler, so it has to outlive pointerup — but a
       * multi-touch gesture produces NO synthesised click at all. After a pinch the flag therefore had
       * nothing to consume it and stayed true, and the next genuine backdrop TAP was swallowed: on
       * touch, "pinch to zoom, then tap outside to close" needed two taps. (Escape and ✕ kept working,
       * which is what hid it.) Clearing it on pointerup instead would break the mouse path, where the
       * click that must still see it fires afterwards.
       *
       * A macrotask is exactly the right window: pointerup → click → this timeout. Whichever happens,
       * the flag is gone before the NEXT gesture can be affected by it.
       */
      if (movedClearTimeoutRef.current !== null) clearTimeout(movedClearTimeoutRef.current);
      // Only for gestures that produce NO synthesised click. On the mouse path the click MUST still see
      // the flag, and consumeMoved() + the next onPointerDown already clear it there — so scheduling a
      // timer for the mouse would rest on `pointerup → click` beating a macrotask. That ordering does
      // hold in all three engines, but relying on it silently is exactly what this narrowing removes.
      if (event.pointerType !== "mouse") {
        movedClearTimeoutRef.current = setTimeout(() => {
          movedRef.current = false;
          movedClearTimeoutRef.current = null;
        }, 0);
      }
    }
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (scaleRef.current > IMAGE_ZOOM_MIN_SCALE) {
      reset();
      return;
    }
    zoomAbout(DOUBLE_CLICK_SCALE, event.clientX, event.clientY);
  };

  /** Zoom from the stage centre — for the −/+ buttons, which have no cursor position of their own. */
  const zoomFromCentre = (nextScale: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    zoomAbout(nextScale, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  /** True if the last gesture moved. Reads once and clears, so a consumer cannot double-count it. */
  const consumeMoved = () => {
    const moved = movedRef.current;
    movedRef.current = false;
    return moved;
  };

  return {
    scale,
    offset,
    stageRef,
    isZoomed: scale > IMAGE_ZOOM_MIN_SCALE,
    reset,
    zoomFromCentre,
    consumeMoved,
    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
    stageHandlers: {
      onDoubleClick,
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
    },
    /**
     * Gesture-END handlers for an ANCESTOR of the stage — spread these on the overlay/backdrop.
     *
     * Pointer capture is the first line of defence, but it can be refused (and does not exist in
     * jsdom), and a finger that lifts on the backdrop fires `pointerup` there, not on the stage:
     * React synthetic events bubble UP, so a parent sees the stage's events but never the reverse.
     * Without a handler up here that lift is simply lost, `pointerMap` never empties, and the
     * gesture-end timer is never scheduled — which is how a pinch could still swallow the next tap.
     *
     * Safe to have both: `endPointer` is idempotent (deleting an absent id is a no-op, and the timer
     * is cleared before it is rescheduled), so a stage lift bubbling up here costs nothing.
     */
    gestureEndHandlers: {
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
    },
  };
};
